import React from "react";

import { MenuItem, Select, FormControl, Theme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import InputLabel from "@mui/material/InputLabel";

import { InlinePriceIndicator } from "../../../../../shared/components/Pricing/InlinePriceIndicator";
import { WlrLocationTypeEnum } from "../../enums";
import { WlrLocationBroadbandProduct } from "../../types";

export interface BaseBroadbandProductSelectProps {
  label?: string;
  dense?: boolean;
  broadbandProductId: string;
  wlrProductId: WlrLocationTypeEnum | string;
  configId: number;
  contractLength: number;
}
export interface BroadbandProductSelectProps
  extends BaseBroadbandProductSelectProps {
  broadbandProducts: WlrLocationBroadbandProduct[];
  onAddBroadbandChange: (configId: number, value: string) => void;
}

export const BroadbandProductSelect = ({
  label,
  dense,
  broadbandProductId,
  wlrProductId,
  configId,
  contractLength,
  broadbandProducts,
  onAddBroadbandChange,
}: BroadbandProductSelectProps) => {
  const classes = useStyles();

  return (
    <FormControl className={classes.formControl} variant={"outlined"}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        data-cy="AddBroadbandSelect"
        classes={{ select: dense ? classes.dense : undefined }}
        label={label}
        value={broadbandProductId}
        onChange={(event) => {
          onAddBroadbandChange(configId, event.target.value as string);
        }}
      >
        <MenuItem value={""}>
          <em>None</em>
        </MenuItem>
        {broadbandProducts.map((p) => {
          if (p.first_broadband_component?.type === "MPF") return null;
          if (
            wlrProductId !== WlrLocationTypeEnum.NEW_FTTP &&
            p.first_broadband_component?.type === "FTTP"
          )
            return null;
          // TP29038: Only show FTTP products if 'New FTTP Provide' is
          // selected.
          // This is superseded by ChooseSingleProduct which deals with FTTP & SOGEA stuff now.
          // Perhaps this logic is still need for something though, so gonna leave in place....
          if (
            wlrProductId === WlrLocationTypeEnum.NEW_FTTP &&
            p.first_broadband_component?.type !== "FTTP"
          )
            return null;
          if (
            contractLength === 3 &&
            p.first_broadband_component.type !== "FTTC"
          )
            return null;
          if (
            (wlrProductId !== WlrLocationTypeEnum.NEW_SOGEA &&
              wlrProductId !== WlrLocationTypeEnum.EXISTING_SOGEA &&
              p.first_broadband_component.type === "SOGEA") ||
            ((wlrProductId === WlrLocationTypeEnum.NEW_SOGEA ||
              wlrProductId === WlrLocationTypeEnum.EXISTING_SOGEA) &&
              p.first_broadband_component.type !== "SOGEA")
          )
            return null;
          return (
            <MenuItem className={classes.menuItem} value={p.id} key={p.id}>
              {
                <div className={classes.product}>
                  <span>
                    <b>{p.name} </b>
                    {p.price.first_bill_recurring_price_with_promotions !==
                      p.recurring_price_without_promotions && (
                      <InlinePriceIndicator style={{ fontSize: 15 }} />
                    )}
                    {p.description}{" "}
                  </span>
                  <span className={classes.price}>
                    £{p.price.first_bill_recurring_price_with_promotions}
                  </span>
                </div>
              }
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  description: {
    fontSize: "0.9em",
    color: theme.palette.text.disabled,
  },
  price: {
    color: theme.palette.success.main,
    fontSize: "0.9em",
    paddingLeft: 8,
  },
  dense: {
    padding: theme.spacing(0.75),
  },
  formControl: {
    width: "200px",
  },
  menuItem: {
    width: "100%",
  },
  product: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    overflow: "hidden",
  },
}));
